import { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    Container,
    Box,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemText,
    Typography,
    useScrollTrigger,
    Slide,
    Button,
    Menu,
    MenuItem,
    Avatar,
    useMediaQuery
} from '@mui/material';
import { Menu as MenuIcon, Close, Translate } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';

const GradientAppBar = styled(AppBar)(({ theme }) => ({
    background: `linear-gradient(135deg, #fff 5%, ${theme.palette.primary.main} 80%)`,
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
    backdropFilter: 'blur(10px)',
    borderBottom: `1px solid ${theme.palette.primary.light}20`
}));

const NavButton = styled(Button)(({ theme, active }) => ({
    position: 'relative',
    color: 'white',
    fontWeight: 600,
    letterSpacing: '0.5px',
    transition: 'all 0.3s ease',
    '&:before': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: active ? '100%' : '0%',
        height: '2px',
        background: 'white',
        transition: 'width 0.3s ease'
    },
    '&:hover': {
        '&:before': {
            width: '100%'
        }
    }
}));

const LanguageButton = styled(Button)(({ theme }) => ({
    color: 'white',
    border: `1px solid ${theme.palette.primary.light}`,
    borderRadius: '20px',
    padding: '4px 12px',
    minWidth: 'auto',
    marginLeft: '12px',
    '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.1)'
    }
}));

const Header = ({ language, onLanguageChange }) => {
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isHomePage = location.pathname === '/';
    const [mobileOpen, setMobileOpen] = useState(false);
    const [activeNav, setActiveNav] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const trigger = useScrollTrigger();

    const navItems = {
        EN: [
            { name: 'Story', target: '#story', path: '/#story' },
            { name: 'Vision & Mission', target: '#vision', path: '/#vision' },
            { name: 'Team', target: '#team', path: '/#team' },
            { name: 'Timeline', target: '#timeline', path: '/#timeline' },
            { name: 'Gallery', target: '#gallery', path: '/#gallery' },
            { name: 'Why Us?', target: '#whyus', path: '/#whyus' },
            { name: 'Coverage', target: '#coverage', path: '/#coverage' },
            { name: 'Contact', target: '#footer', path: '/#footer' }
        ],
        BM: [
            { name: 'Cerita', target: '#story', path: '/#story' },
            { name: 'Visi & Misi', target: '#vision', path: '/#vision' },
            { name: 'Pasukan', target: '#team', path: '/#team' },
            { name: 'Garis Masa', target: '#timeline', path: '/#timeline' },
            { name: 'Galeri', target: '#gallery', path: '/#gallery' },
            { name: 'Kenapa Kami?', target: '#whyus', path: '/#whyus' },
            { name: 'Liputan', target: '#coverage', path: '/#coverage' },
            { name: 'Hubungi', target: '#footer', path: '/#footer' }
        ],
        CN: [
            { name: '故事', target: '#story', path: '/#story' },
            { name: '愿景与使命', target: '#vision', path: '/#vision' },
            { name: '团队', target: '#team', path: '/#team' },
            { name: '时间线', target: '#timeline', path: '/#timeline' },
            { name: '画廊', target: '#gallery', path: '/#gallery' },
            { name: '为何选择我们', target: '#whyus', path: '/#whyus' },
            { name: '媒体报道', target: '#coverage', path: '/#coverage' },
            { name: '联系', target: '#footer', path: '/#footer' }
        ]
    };

    useEffect(() => {
        setActiveNav(location.hash);
    }, [location]);

    const handleNavClick = (item) => {
        setActiveNav(item.target);
        if (isHomePage) {
            const element = document.querySelector(item.target);
            element?.scrollIntoView({ behavior: 'smooth' });
        } else {
            navigate('/');
            setTimeout(() => {
                document.querySelector(item.target)?.scrollIntoView();
                setActiveNav(item.target);
            }, 100);
        }
    };

    const handleLogoClick = () => {
        setActiveNav('');
        if (isHomePage) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            navigate('/');
        }
    };

    const handleLanguageMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleLanguageMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLanguageChange = (lang) => {
        onLanguageChange(lang);
        handleLanguageMenuClose();
    };

    const LanguageMenu = () => (
        <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleLanguageMenuClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
        >
            <MenuItem onClick={() => handleLanguageChange('EN')}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                        src="/images/flags/en.png"
                        sx={{ width: 24, height: 24, mr: 1 }}
                    />
                    English
                </Box>
            </MenuItem>
            <MenuItem onClick={() => handleLanguageChange('BM')}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                        src="/images/flags/my.png"
                        sx={{ width: 24, height: 24, mr: 1 }}
                    />
                    Bahasa Malaysia
                </Box>
            </MenuItem>
            <MenuItem onClick={() => handleLanguageChange('CN')}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                        src="/images/flags/cn.png"
                        sx={{ width: 24, height: 24, mr: 1 }}
                    />
                    中文
                </Box>
            </MenuItem>
        </Menu>
    );

    const drawer = (
        <Box sx={{
            width: 320,
            height: '100%',
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
            color: 'white'
        }}>
            <Box sx={{
                p: 3,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid rgba(255,255,255,0.1)`
            }}>
                <img
                    src="/images/logo.png"
                    alt="logo"
                    style={{ height: 40 }}
                />
                <Box>
                    <LanguageButton
                        startIcon={<Translate sx={{ fontSize: '1rem' }} />}
                        onClick={handleLanguageMenuOpen}
                        size="small"
                    >
                        {language}
                    </LanguageButton>
                    <IconButton
                        onClick={() => setMobileOpen(false)}
                        sx={{
                            color: 'white',
                            ml: 1
                        }}
                    >
                        <Close />
                    </IconButton>
                </Box>
            </Box>
            <List>
                {navItems[language].map((item) => (
                    <ListItem
                        key={item.name}
                        button
                        onClick={() => {
                            handleNavClick(item);
                            setMobileOpen(false);
                        }}
                        sx={{
                            py: 2,
                            px: 3,
                            background: activeNav === item.target ? 'rgba(255,255,255,0.1)' : 'transparent',
                            '&:hover': {
                                background: 'rgba(255,255,255,0.05)'
                            }
                        }}
                    >
                        <ListItemText
                            primary={
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 500,
                                        color: activeNav === item.target ? theme.palette.secondary.main : 'white'
                                    }}
                                >
                                    {item.name}
                                </Typography>
                            }
                        />
                    </ListItem>
                ))}
            </List>
        </Box>
    );

    return (
        <Slide appear={false} direction="down" in={!trigger}>
            <GradientAppBar position="sticky">
                <Container maxWidth="xl">
                    <Toolbar disableGutters sx={{ py: 1 }}>
                        {/* Logo */}
                        <Box
                            sx={{
                                flexGrow: 1,
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                transition: 'transform 0.3s ease',
                                '&:hover': { transform: 'scale(1.05)' }
                            }}
                            onClick={handleLogoClick}
                        >
                            <img
                                src="/images/logo.png"
                                alt="logo"
                                style={{ height: 50 }}
                            />
                        </Box>

                        {/* Desktop Navigation */}
                        <Box sx={{
                            display: { xs: 'none', md: 'flex' },
                            alignItems: 'center',
                            gap: 2
                        }}>
                            {navItems[language].map((item) => (
                                <NavButton
                                    key={item.name}
                                    onClick={() => handleNavClick(item)}
                                    active={activeNav === item.target}
                                >
                                    {item.name}
                                </NavButton>
                            ))}

                            <LanguageButton
                                startIcon={<Translate />}
                                onClick={handleLanguageMenuOpen}
                                sx={{ ml: 2 }}
                            >
                                {language}
                            </LanguageButton>
                        </Box>

                        {/* Mobile Menu Button */}
                        <Box sx={{ display: { md: 'none' } }}>
                            <LanguageButton
                                startIcon={<Translate sx={{ fontSize: '1rem' }} />}
                                onClick={handleLanguageMenuOpen}
                                size="small"
                            >
                                {language}
                            </LanguageButton>
                            <IconButton
                                aria-label="open menu"
                                edge="end"
                                onClick={() => setMobileOpen(true)}
                                sx={{ color: 'white', ml: 1 }}
                            >
                                <MenuIcon />
                            </IconButton>
                        </Box>
                    </Toolbar>
                </Container>

                {/* Mobile Drawer */}
                <Drawer
                    anchor="right"
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    PaperProps={{ sx: { boxShadow: 24 } }}
                >
                    {drawer}
                </Drawer>

                {/* Shared Language Menu */}
                <LanguageMenu />
            </GradientAppBar>
        </Slide>
    );
};

Header.propTypes = {
    language: PropTypes.oneOf(['EN', 'BM', 'CN']).isRequired,
    onLanguageChange: PropTypes.func.isRequired
};

export default Header;